/*
 * Columnas del reporte PF que el usuario ve y edita, agrupadas por pestaña
 * (§7 del diseño), más los textos de ayuda y los catálogos de Buró.
 *
 * Las claves de `valores` son el número de columna de Buró ('1'..'71'). Las
 * columnas que ocupan varias posiciones (15–16 defunción, 27–34 domicilio del
 * empleo) se muestran y se editan por la primera; sus errores se buscan en todo
 * el rango. 'tipo_persona' no existe en el CSV: solo decide si la fila entra.
 *
 * Los catálogos son los de CatalogosBuro.php (backend): si cambian allá, se
 * cambian aquí.
 */

const EC = [['', '—'], ['S', 'S · Soltero'], ['M', 'M · Casado'], ['D', 'D · Divorciado'], ['W', 'W · Viudo'], ['F', 'F · Unión libre']];
const SX = [['', '—'], ['M', 'M · Masculino'], ['F', 'F · Femenino']];
const TP = [['', '—'], ['PF', 'PF · Persona física'], ['PFAE', 'PFAE · Persona física con actividad empresarial'], ['PM', 'PM · Persona moral']];
export const EDO = [['', '—'], ...[
  ['AGS', 'Aguascalientes'], ['BCN', 'Baja California'], ['BCS', 'Baja California Sur'], ['CAM', 'Campeche'],
  ['CHS', 'Chiapas'], ['CHI', 'Chihuahua'], ['CDMX', 'Ciudad de México'], ['COA', 'Coahuila'], ['COL', 'Colima'],
  ['DGO', 'Durango'], ['EM', 'Estado de México'], ['GTO', 'Guanajuato'], ['GRO', 'Guerrero'], ['HGO', 'Hidalgo'],
  ['JAL', 'Jalisco'], ['MICH', 'Michoacán'], ['MOR', 'Morelos'], ['NAY', 'Nayarit'], ['NL', 'Nuevo León'],
  ['OAX', 'Oaxaca'], ['PUE', 'Puebla'], ['QRO', 'Querétaro'], ['QR', 'Quintana Roo'], ['SLP', 'San Luis Potosí'],
  ['SIN', 'Sinaloa'], ['SON', 'Sonora'], ['TAB', 'Tabasco'], ['TAM', 'Tamaulipas'], ['TLAX', 'Tlaxcala'],
  ['VER', 'Veracruz'], ['YUC', 'Yucatán'], ['ZAC', 'Zacatecas'],
].map(([c, n]) => [c, `${c} · ${n}`])];

// Anexo 3 del manual de Buró (Formas de pago: MOP). Los atrasos se miden contra la fecha límite de pago.
const MOP = [
  ['', '—'], ['00', '00 · Muy reciente para calificar (sin actividad)'], ['01', '01 · Al corriente'],
  ['02', '02 · Atraso de 1 a 29 días'], ['03', '03 · Atraso de 30 a 59 días'], ['04', '04 · Atraso de 60 a 89 días'],
  ['05', '05 · Atraso de 90 a 119 días'], ['06', '06 · Atraso de 120 a 149 días'], ['07', '07 · Atraso de 150 días a 12 meses'],
  ['96', '96 · Atraso de más de 12 meses'], ['97', '97 · Deuda parcial o total sin recuperar'],
  ['99', '99 · Fraude cometido por el cliente'], ['UR', 'UR · Cuenta sin información (sin actividad)'],
];

// Tipo de contrato: códigos de CatalogosBuro.php con la descripción del Anexo 2 del
// manual de Buró (la marca "(PFAE)" es del manual: producto de persona con actividad
// empresarial). PA y PG existen en el manual pero no en el catálogo del backend.
const TC = [['', '—'], ...[
  ['AF', 'Aparatos / Muebles'], ['AG', 'Agropecuario (PFAE)'], ['AL', 'Arrendamiento automotriz'], ['AP', 'Aviación'],
  ['AU', 'Compra de automóvil'], ['BD', 'Fianza'], ['BT', 'Bote / Lancha'], ['CC', 'Tarjeta de crédito'],
  ['CE', 'Cartas de crédito (PFAE)'], ['CF', 'Crédito fiscal'], ['CL', 'Línea de crédito'], ['CO', 'Consolidación'],
  ['CS', 'Crédito simple (PFAE)'], ['CT', 'Con colateral (PFAE)'], ['DE', 'Descuentos (PFAE)'], ['EQ', 'Equipo'],
  ['FI', 'Fideicomiso (PFAE)'], ['FT', 'Factoraje'], ['HA', 'Habilitación o avío (PFAE)'], ['HE', 'Préstamo tipo "Home Equity"'],
  ['HI', 'Mejoras a la casa'], ['LR', 'Línea de crédito reinstalable'], ['LS', 'Arrendamiento'],
  ['OA', 'Otros adeudos vencidos (PFAE)'], ['PB', 'Editorial'], ['PL', 'Préstamo personal'], ['PN', 'Préstamo de nómina'],
  ['PR', 'Prendario (PFAE)'], ['PS', 'Pago de servicios'], ['PQ', 'Quirografario (PFAE)'], ['RC', 'Reestructurado (PFAE)'],
  ['RD', 'Redescuento (PFAE)'], ['RE', 'Bienes raíces'], ['RF', 'Refaccionario (PFAE)'], ['RN', 'Renovado (PFAE)'],
  ['RV', 'Vehículo recreativo'], ['SC', 'Tarjeta garantizada'], ['SE', 'Préstamo garantizado'], ['SG', 'Seguros'],
  ['SM', 'Segunda hipoteca'], ['ST', 'Préstamo para estudiante'], ['TE', 'Tarjeta de crédito empresarial'],
  ['US', 'Préstamo no garantizado'],
].map(([k, d]) => [k, `${k} · ${d}`])];

// Frecuencia de pagos (Etiqueta 11 del segmento de cuenta, pagos fijos e hipoteca). Ojo: H es
// Semestral aquí; "Por hora" solo existe en el periodo de pago del sueldo, que es otra columna.
const FREC = [['', '—'], ...[
  ['B', 'Bimestral'], ['D', 'Diario'], ['H', 'Semestral'], ['K', 'Catorcenal'], ['M', 'Mensual'],
  ['P', 'Deducción del salario (cada que recibe su sueldo)'], ['Q', 'Trimestral'], ['S', 'Quincenal (2 veces al mes)'],
  ['V', 'Variable'], ['W', 'Semanal'], ['Y', 'Anual'],
].map(([k, d]) => [k, `${k} · ${d}`])];

// Capital de cada estado (por clave de Buró) para sugerir ciudad y municipio.
export const CAPITALES = {
  AGS: 'AGUASCALIENTES', BCN: 'MEXICALI', BCS: 'LA PAZ', CAM: 'SAN FRANCISCO DE CAMPECHE', CHS: 'TUXTLA GUTIERREZ',
  CHI: 'CHIHUAHUA', CDMX: 'CIUDAD DE MEXICO', COA: 'SALTILLO', COL: 'COLIMA', DGO: 'VICTORIA DE DURANGO', EM: 'TOLUCA',
  GTO: 'GUANAJUATO', GRO: 'CHILPANCINGO DE LOS BRAVO', HGO: 'PACHUCA DE SOTO', JAL: 'GUADALAJARA', MICH: 'MORELIA',
  MOR: 'CUERNAVACA', NAY: 'TEPIC', NL: 'MONTERREY', OAX: 'OAXACA DE JUAREZ', PUE: 'PUEBLA', QRO: 'QUERETARO',
  QR: 'CHETUMAL', SLP: 'SAN LUIS POTOSI', SIN: 'CULIACAN', SON: 'HERMOSILLO', TAB: 'VILLAHERMOSA',
  TAM: 'CIUDAD VICTORIA', TLAX: 'TLAXCALA', VER: 'XALAPA', YUC: 'MERIDA', ZAC: 'ZACATECAS',
};

// Líneas de ayuda para columnas que solo se cambian en el reporte.
export const AYUDA_SOLO_REPORTE = {
  62: 'Se fija al cerrar el mes; no cambia entre meses.',
};

export const EMPLEO_SIN_EMPRESA = [
  'TRABAJADOR INDEPENDIENTE', 'JUBILADO', 'ESTUDIANTE', 'LABOR DE HOGAR', 'DESEMPLEADO', 'EXEMPLEADO',
];

export const AYUDA = {
  42: 'Qué tipo de producto es el contrato. En PEICAR casi siempre es AL (arrendamiento automotriz) o AU (compra de automóvil).',
  45: 'Cada cuánto paga el cliente según su contrato. PEICAR casi siempre es M (mensual).',
  57: 'Calificación de atraso: 01 al corriente, 02 de 1 a 29 días, 03 de 30 a 59, 04 de 60 a 89, 05 de 90 a 119, 06 de 120 a 149, 07 de 150 días a 12 meses. 00 cuenta muy nueva, UR sin información; 96 más de 12 meses de atraso, 97 deuda sin recuperar, 99 fraude del cliente.',
  53: 'Capital de la tabla de amortización (ya incluye el residual), más el interés e IVA de lo vencido y la mora al día del corte.',
  63: 'La columna «Saldo» de la tabla de amortización, menos lo que ya abonó a capital si una cuota está pagada a medias (igual que en Cartera): solo capital, sin intereses ni IVA. No puede ser mayor que el saldo actual.',
  55: 'Lo que ya debió pagar y no pagó al día del corte.',
  62: 'Fecha de la primera renta que no pagó a tiempo. 01011900 = nunca se ha atrasado. No debe cambiar entre meses.',
  26: 'Dónde trabaja. Si no aplica: TRABAJADOR INDEPENDIENTE, JUBILADO, ESTUDIANTE, LABOR DE HOGAR, DESEMPLEADO o EXEMPLEADO.',
  tipo_persona: 'Indica si es PF, PFAE o PM. Solo las PF van en este reporte.',
};

// k: clave en `valores` · num: texto del encabezado · hasta: última columna del
// rango · cl: dato del cliente (admite "corregir también en el sistema") ·
// kind: text | amount | int · w: ancho mínimo en px.
const C = (k, label, num, o = {}) => ({ k: String(k), label, num: num ?? String(k), w: 120, kind: 'text', ...o });

export const TABS = {
  persona: [
    C(4, 'Ap. paterno', null, { cl: 1 }),
    C(5, 'Ap. materno', null, { cl: 1 }),
    C(7, 'Primer nombre', null, { cl: 1 }),
    C(8, 'Segundo nombre', null, { cl: 1, w: 130 }),
    C(9, 'Nacimiento', null, { cl: 1, w: 110, tipo: 'fecha' }),
    C(10, 'RFC', null, { cl: 1, w: 140, tipo: 'rfc' }),
    C(13, 'Estado civil', null, { cl: 1, opts: EC, w: 110 }),
    C(14, 'Sexo', null, { cl: 1, opts: SX, w: 80 }),
    C(15, 'Defunción', '15–16', { w: 110, hasta: 16, tipo: 'fecha' }),
    C('tipo_persona', 'Tipo de persona', '*', { cl: 1, col: 0, opts: TP, w: 140 }),
  ],
  domicilio: [
    C(17, 'Calle y número', null, { cl: 1, w: 220, dom: 'calle' }),
    C(18, 'Complemento', null, { cl: 1, dom: 'complemento' }),
    C(19, 'Colonia', null, { cl: 1, w: 170, tipo: 'lugar', dom: 'colonia' }),
    C(20, 'Municipio', null, { cl: 1, w: 170, tipo: 'lugar', dom: 'municipio' }),
    C(21, 'Ciudad', null, { cl: 1, w: 160, tipo: 'lugar', dom: 'ciudad' }),
    C(22, 'Estado', null, { cl: 1, opts: EDO, w: 150, dom: 'estado' }),
    C(23, 'C.P.', null, { cl: 1, w: 80, tipo: 'cp', dom: 'cp' }),
    C(24, 'Teléfono', null, { cl: 1, tipo: 'tel' }),
  ],
  empleo: [
    C(26, 'Empresa', null, { cl: 1, w: 240, tipo: 'empresa' }),
    C(27, 'Domicilio del empleo', '27–34', { w: 240, hasta: 34 }),
    C(35, 'Salario', null, { kind: 'amount', w: 100 }),
  ],
  cuenta: [
    C(42, 'Tipo contrato', null, { w: 110, opts: TC }),
    C(44, 'Núm. pagos', null, { kind: 'int', w: 100 }),
    C(45, 'Frecuencia', null, { w: 100, opts: FREC }),
    C(46, 'Apertura', null, { w: 100, tipo: 'fecha' }),
    C(47, 'Renta', null, { kind: 'amount', w: 100 }),
    C(48, 'Último pago', null, { w: 110, tipo: 'fecha' }),
    C(50, 'Cierre', null, { w: 90, tipo: 'fecha' }),
    C(52, 'Crédito máximo', null, { kind: 'amount', w: 130 }),
    C(53, 'Saldo actual', null, { kind: 'amount', w: 130 }),
    C(55, 'Saldo vencido', null, { kind: 'amount', w: 130 }),
    C(56, 'Pagos vencidos', null, { kind: 'int', w: 130 }),
    C(57, 'MOP', null, { opts: MOP, w: 80 }),
    C(58, 'Clave observación', null, { w: 140 }),
    C(62, 'Primer incumplimiento', null, { w: 170, tipo: 'fecha' }),
    C(63, 'Capital insoluto', null, { kind: 'amount', w: 140 }),
    C(64, 'Monto último pago', null, { kind: 'amount', w: 150 }),
    C(65, 'Plazo', null, { kind: 'int', w: 80 }),
    C(66, 'Monto original', null, { kind: 'amount', w: 130 }),
    C(71, 'Correo', null, { cl: 1, w: 220, tipo: 'correo' }),
  ],
};

// Domicilio del cliente: rol → columna (como clavesDomicilio de PM). El editor de celda lo muestra en un solo bloque.
export const DOMICILIO_PF = { calle: '17', complemento: '18', colonia: '19', municipio: '20', ciudad: '21', estado: '22', cp: '23' };

export const PESTANAS = [
  ['persona', 'Persona'], ['domicilio', 'Domicilio'], ['empleo', 'Empleo'], ['cuenta', 'Cuenta'], ['todas', 'Todas'],
];

const EDITABLES = [...TABS.persona, ...TABS.domicilio, ...TABS.empleo, ...TABS.cuenta];
export const COL = {};
EDITABLES.forEach((c) => { COL[c.k] = c; });

// Columnas fijas del formato (no se editan). El 6 y el 49 no están en el
// prototipo ni son fijas en ColumnasPf: ver "Dudas" del reporte de trabajo; en
// "Todas" se dejan editables como texto para que un error ahí no se quede sin salida.
const FIJAS = [1, 2, 3, 11, 12, 25, 36, 37, 38, 39, 40, 41, 43, 51, 54, 59, 60, 61, 67, 68, 69, 70];
const SUELTAS = [C(6, 'Apellido adicional'), C(49, 'Fecha última compra', null, { w: 140, tipo: 'fecha' })];

// Pestaña "Todas": las 71 posiciones en orden, sin tipo_persona.
export const TODAS = [
  ...EDITABLES.filter((c) => c.k !== 'tipo_persona'),
  ...SUELTAS,
  ...FIJAS.map((n) => ({ k: String(n), label: 'Campo fijo', num: String(n), w: 100, kind: 'text', fija: true })),
].sort((a, b) => parseInt(a.num, 10) - parseInt(b.num, 10));

// Campos de la ficha del cliente (P4b), por sección.
export const FICHA = {
  persona: ['4', '5', '7', '8', '9', '10', '14', '13'],
  domicilio: ['17', '18', '23', '19', '20', '21', '22', '24'],
  empleo: ['26'],
};
export const FICHA_CLAVES = [...FICHA.persona, ...FICHA.domicilio, ...FICHA.empleo, 'tipo_persona'];

export const esNumerica = (c) => ['amount', 'int', 'decimal'].includes(c.kind);

// Errores de una columna (o de todo su rango).
export const erroresDe = (fila, c) => {
  const desde = parseInt(c.k, 10);
  if (Number.isNaN(desde)) return (fila.errores || []).filter((e) => Number(e.columna) === 0);
  const hasta = c.hasta || desde;
  return (fila.errores || []).filter((e) => {
    const n = parseInt(e.columna, 10);
    return n >= desde && n <= hasta;
  });
};

/*
 * Reglas que NO son un dato mal capturado, sino un aviso de "confirmar con Buró"
 * antes de cerrar (VR con quebranto, FR con faltante). El backend los deja en
 * `errores` —así que siguen bloqueando el cierre— pero en el front se pintan
 * como advertencia (amarillo) y no como error rojo de dato faltante.
 */
export const REGLAS_AVISO = ['pendiente_buro_vr', 'pendiente_buro_fr'];
export const esAviso = (e) => REGLAS_AVISO.includes(e?.regla);
// Separa los errores de una celda/fila en avisos de confirmación y errores reales.
export const partirErrores = (errores) => ({
  avisos: (errores || []).filter(esAviso),
  reales: (errores || []).filter((e) => !esAviso(e)),
});

// Valor mostrado de una celda. El tipo de persona no viene en `valores`: un
// error de columna 0 significa que falta; si no hay error, la fila es PF.
export const valorDe = (fila, c) => {
  if (c.k === 'tipo_persona') return fila.tipo_persona ?? (erroresDe(fila, c).length ? '' : 'PF');
  if (c.lista) {
    const indices = new Set(Object.entries(fila.valores || {}).filter(([k, v]) => Number(k) >= Number(c.k) && Number(k) <= c.hasta && String(v ?? '').trim() !== '').map(([k]) => Math.floor(Number(k) / 100) % 10));
    return `${indices.size} ${(indices.size === 1 && c.singular ? c.singular : c.label).toLowerCase()}`;
  }
  return fila.valores[c.k] ?? '';
};

// Número de columna que se manda al PATCH (0 = tipo de persona).
export const columnaApi = (c) => (c.k === 'tipo_persona' ? 0 : Number(c.k));

// Estado de color de una celda: exc | err | sug | man | sys | fix.
export const estadoCelda = (fila, c) => {
  // Solo los errores "reales" pintan rojo; los avisos de confirmación van amarillos.
  const { avisos, reales } = partirErrores(erroresDe(fila, c));
  // Un error en una columna fija (p. ej. la 39) también debe verse en rojo.
  if (c.fija) return fila.incluir && reales.length ? 'err' : 'fix';
  if (!fila.incluir) return 'exc';
  const fuentes = c.lista ? Object.entries(fila.fuentes || {}).filter(([k]) => Number(k) >= Number(c.k) && Number(k) <= c.hasta).map(([, v]) => v) : [(fila.fuentes || {})[c.k]];
  const fuente = ['falta', 'sugerencia', 'manual'].find((v) => fuentes.includes(v)) || fuentes[0];
  if (reales.length || fuente === 'falta') return 'err';
  if (avisos.length || fuente === 'sugerencia') return 'sug';
  if (fuente === 'manual') return 'man';
  return 'sys';
};

// Estado de una fila: excl | err | sug | ok.
export const estadoFila = (fila) => {
  if (!fila.incluir) return 'excl';
  const { avisos, reales } = partirErrores(fila.errores);
  if (reales.length || Object.values(fila.fuentes || {}).includes('falta')) return 'err';
  if (avisos.length || Object.values(fila.fuentes || {}).includes('sugerencia')) return 'sug';
  return 'ok';
};

const sinAcentos = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '');

// Búsqueda: sin acentos ni mayúsculas.
export const norm = (s) => sinAcentos(s).toUpperCase().replace(/\s+/g, ' ').trim();

// Vista previa de "Se enviará como". Aproxima NormalizadorBuro (backend), que
// es quien manda: mayúsculas, sin acentos ni comas, montos enteros.
export const seEnviaComo = (c, v) => {
  if (c.kind === 'decimal') return v === '' || v == null ? '' : (Number.isFinite(Number(v)) ? Number(v).toFixed(2).padStart(6, '0') : String(v));
  if (c.kind === 'amount') {
    if (v === '' || v == null) return '';
    return String(Math.round(Number(String(v).replace(/[$,\s]/g, '')) || 0));
  }
  if (c.tipo === 'correo') return String(v ?? '').trim();
  if (c.kind === 'int') return String(v ?? '').replace(/\D/g, '');
  return sinAcentos(v).toUpperCase().replace(/,/g, '').replace(/\s+/g, ' ').trim();
};

// ── Fechas: Buró usa DDMMAAAA; el input type=date, AAAA-MM-DD ──
export const aIso = (v) => {
  const m = /^(\d{2})(\d{2})(\d{4})$/.exec(String(v ?? ''));
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};
export const deIso = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v ?? ''));
  return m ? `${m[3]}${m[2]}${m[1]}` : '';
};
export const verFecha = (v) => {
  const m = /^(\d{2})(\d{2})(\d{4})$/.exec(String(v ?? ''));
  return m ? `${m[1]}/${m[2]}/${m[3]}` : String(v ?? '');
};

// Fecha de nacimiento (DDMMAAAA) desde un RFC de persona física (AAAA YYMMDD XXX).
// El siglo se deduce: quien nació en 20YY ya es mayor de edad solo si YY <= año actual - 18.
export const nacimientoDeRfc = (rfc) => {
  const m = /^[A-ZÑ&]{4}(\d{2})(\d{2})(\d{2})[A-Z0-9]{3}$/.exec(String(rfc ?? '').trim().toUpperCase());
  if (!m) return '';
  const yy = Number(m[1]);
  const anio = (yy <= (new Date().getFullYear() % 100) - 18 ? 2000 : 1900) + yy;
  const f = new Date(anio, Number(m[2]) - 1, Number(m[3]));
  if (f.getFullYear() !== anio || f.getMonth() !== Number(m[2]) - 1 || f.getDate() !== Number(m[3])) return '';
  return `${m[3]}${m[2]}${anio}`;
};

// Revisión en vivo RFC vs nacimiento (reglas rfc_formato y rfc_fecha del backend),
// con lo que hay en el formulario y no con lo último guardado.
// estado: 'indeterminado' (falta RFC o nacimiento: valen los errores del servidor)
//   | 'formato' | 'no_coincide' | 'ok'.
export const REGLAS_RFC = ['rfc_formato', 'rfc_fecha'];
export const rfcVsNacimiento = (rfcCrudo, nacimiento) => {
  const rfc = String(rfcCrudo ?? '').trim().toUpperCase();
  if (!rfc) return { estado: 'indeterminado' };
  const m = /^[A-ZÑ&]{4}(\d{2})(\d{2})(\d{2})([A-Z0-9]{3})?$/.exec(rfc);
  if (!m) return { estado: 'formato', mensaje: 'El RFC debe llevar 4 letras, 6 dígitos de fecha (AAMMDD) y, si ya la tienes, la homoclave de 3 caracteres.' };
  const n = /^(\d{2})(\d{2})(\d{4})$/.exec(String(nacimiento ?? ''));
  if (!n) return { estado: 'indeterminado' };
  if (n[3].slice(2) === m[1] && n[2] === m[2] && n[1] === m[3]) return { estado: 'ok' };
  return {
    estado: 'no_coincide',
    mensaje: `El RFC lleva ${m[3]}/${m[2]}/${m[1]} (día/mes/año) y el nacimiento es ${n[1]}/${n[2]}/${n[3]}.`,
  };
};
